import { CalendarEntryExtended } from 'owa-calendar-cache';
import { isDefaultMailbox } from 'owa-client-ids';
import { isFeatureEnabled } from 'owa-feature-flags';
import LinkedCalendarEntry from 'owa-service/lib/contract/LinkedCalendarEntry';
import LocalCalendarEntry from 'owa-service/lib/contract/LocalCalendarEntry';
import SubscriptionParameters from 'owa-service/lib/contract/SubscriptionParameters';
import SubscriptionMailboxType from 'owa-service/lib/contract/SubscriptionMailboxType';
import { isOutlookConsumerAccount, isConnectedAccount } from 'owa-accounts-store';

/**
 * Return the folder, mailbox id and type needed for each particular type of calendar item subscription.
 */
export default function getSubscriptionFolderIdAndMailboxDetails(
    calendarEntry: CalendarEntryExtended
): Pick<SubscriptionParameters, 'FolderId' | 'MailboxId' | 'MailboxType'> | null {
    // For group calendars the group name is sufficient.
    // The server will subscribe using OWA's CalendarItemBrokerHandler and the Broker's OwaCalendarItemNotificationHandler.
    if (calendarEntry.IsGroupMailboxCalendar) {
        return isFeatureEnabled('cal-groupNotifications')
            ? { MailboxId: (calendarEntry as LinkedCalendarEntry).OwnerEmailAddress }
            : null;
    }

    // For local calendars on the primary account, use the folderId.
    // The server will subscribe using the MAPI notification handler directly.
    // For calendars from a consumer account, we need to send the folderId,
    // mailbox id and the mailbox type.
    const { CalendarFolderId } = calendarEntry as LocalCalendarEntry;
    if (CalendarFolderId) {
        const primarySmtpAddress = calendarEntry.id.mailboxInfo.primarySmtpAddress;
        return isDefaultMailbox(primarySmtpAddress)
            ? { FolderId: CalendarFolderId.Id }
            : isConnectedAccount(primarySmtpAddress)
            ? {
                  FolderId: CalendarFolderId.Id,
                  MailboxId: primarySmtpAddress,
                  MailboxType: isOutlookConsumerAccount(primarySmtpAddress)
                      ? SubscriptionMailboxType.ConsumerSecondaryMailbox
                      : SubscriptionMailboxType.GmailSecondaryMailbox,
              }
            : null;
    }

    // If we add support for other types of calendars, include their case here.
    // For example, can we subscribe to "old-model" shared calendars, where we have a LinkedCalendarEntry
    // that is NOT a group mailbox and has the owner's e-mail address in OwnerEmailAddress?
    // I've tried, but got "The notification receiver does not have enough permission to make this subscription."

    // Unsupported cases should return null so we don't bombard the server with requests that cannot be fulfilled.
    return null;
}
